import { isApiError } from './api.types';

export namespace AuthTypes {
  /**
   * What the IDP hands back once a user has authenticated, and nothing more. Deliberately
   * just the credential: the app learns *who* the user is from the profile call.
   */
  export type Credentials = {
    /** The Cognito **access** token — what `compliance-api` verifies (CE-14394). */
    accessToken: string;
    /** When `accessToken` stops being accepted, as epoch milliseconds. */
    expiresAt: number;
  };

  /** A completed code exchange, plus the deep link the user set off from. */
  export type SignInResult = {
    credentials: Credentials;
    /** The path `$signIn` was given, carried through the IDP round trip in OAuth `state`. */
    returnTo: string | null;
  };

  /**
   * The identity provider behind `services/auth`. Cognito in every real build; an offline
   * stand-in from `src/mocks` under `VITE_ENABLE_MOCKS=true`. Failures reject with an
   * `ApiTypes.Error` whose `message` is fit to show on the login page, and whose `code`
   * says whether the IDP answered no — see `isIdpRefusal`.
   */
  export type Idp = {
    /** The stored session — refreshed first if its access token has expired — or `null`. */
    restore: () => Promise<Credentials | null>;
    /** Leaves the page for the IDP. Only ever settles by rejecting, if the redirect cannot start. */
    signIn: (returnTo: string | null) => Promise<void>;
    /** Exchanges the authorization code the IDP redirected back with. */
    completeSignIn: () => Promise<SignInResult>;
    /** A fresh access token from the refresh-token grant. */
    refresh: () => Promise<Credentials>;
    /** Drops the stored session from this browser, and nothing else. */
    forget: () => Promise<void>;
    /** `forget`, then leaves the page to end the session at the IDP as well. */
    signOut: () => Promise<void>;
  };

  /** The signed-in user, as the app displays them. */
  export type User = {
    id: string;
    email: string;
    name: string;
  };
}

/**
 * The `code` on a failure the IDP *answered* — a refused refresh token, a declined user, a
 * build with no config. Retrying will not change the answer. A failure without it never
 * reached the IDP (offline, a timeout), and is worth another try with the session kept.
 */
export const IDP_REFUSED_CODE = 'idp_refused';

export const isIdpRefusal = (err: unknown) => isApiError(err) && err.code === IDP_REFUSED_CODE;
